/**
 * Data access for the saya module.
 *
 * Types come from the Worker's contract file
 * (`src/worker/api/saya.contract.ts`), so the API and the screens are typed
 * from a single source — same arrangement as `features/modeller/api.ts`.
 *
 * The whole area is session-gated but NOT role-gated: every account may
 * digitize. There is no approval workflow — a saya set is a living document —
 * and only scan calibration asks for an admin.
 */

import { apiFetch } from '@app/lib/api'
import type {
  DxfImportResult,
  PiecePose,
  SaveScanPartsRequest,
  SayaModelPayload,
  SayaOkResponse,
  SayaPartDto,
  SayaRevisionDetail,
  SayaScanDto,
  ScanQuality,
  SuggestScanRequest,
  SuggestScanResponse,
  TracedPartInput,
} from '../../../worker/api/saya.contract'

export type {
  BomLineOption,
  DxfImportResult,
  Handedness,
  Orientation,
  PartFeature,
  PartGeom,
  PiecePose,
  PieceSuggestion,
  PurchaseUnit,
  RevisionSource,
  RevisionStatus,
  Ring,
  SayaMaterialDto,
  SayaModelPayload,
  SayaPartDto,
  SayaRevisionDetail,
  SayaRevisionSummary,
  SayaScanDto,
  ScanQuality,
  ScanQualityFlag,
  SuggestedMaterial,
  SuggestModelUsed,
  SuggestPieceInput,
  SuggestScanRequest,
  SuggestScanResponse,
  TracedPartInput,
  TraceFinding,
  TraceFindingKind,
  TraceReview,
  TraceReviewVerdict,
} from '../../../worker/api/saya.contract'

export {
  HANDEDNESS_VALUES,
  MAX_MASTER_SIZE,
  MIN_MASTER_SIZE,
  ORIENTATION_VALUES,
  REVISION_STATUSES,
} from '../../../worker/api/saya.contract'

export const sayaKeys = {
  all: ['saya'] as const,
  model: (modelKod: string) => ['saya', 'model', modelKod] as const,
}

/**
 * Puts a revision a mutation just answered with back into the cached payload.
 *
 * Several routes answer with the whole revision precisely because a change to
 * one sheet moves figures the screen must not re-derive — the part count and
 * the net desi per material are computed by the Worker from the rows it just
 * wrote. Refetching would get the same answer one round trip later; recomputing
 * them in the browser is how this screen and the model card would come to
 * disagree about the same set.
 *
 * The history strip is updated from the same object: a `SayaRevisionDetail` IS
 * a `SayaRevisionSummary` with more on it.
 */
export function withRevision(
  payload: SayaModelPayload,
  revision: SayaRevisionDetail,
): SayaModelPayload {
  return {
    ...payload,
    draft: payload.draft?.id === revision.id ? revision : payload.draft,
    current: payload.current?.id === revision.id ? revision : payload.current,
    revisions: payload.revisions.map((summary) =>
      summary.id === revision.id ? revision : summary,
    ),
  }
}

/* ── Reads ──────────────────────────────────────────────────────────────────── */

/**
 * Everything the model card and the review screen need, in one request — the
 * module's ONLY read. The revisions, their parts and the material list all
 * arrive here, so nothing else has a second cache entry that could disagree
 * with this one about the draft in front of the operator.
 */
export function fetchSayaModel(modelKod: string): Promise<SayaModelPayload> {
  return apiFetch<SayaModelPayload>(`/saya/model/${encodeURIComponent(modelKod)}`)
}

/**
 * The scan image itself. It is streamed from R2 behind the session gate, so it
 * is a plain same-origin `<img>` source rather than something fetched here.
 */
export function sayaScanImageUrl(scanId: number): string {
  return `/api/saya/scans/${scanId}/image`
}

/* ── Writes ─────────────────────────────────────────────────────────────────── */

export interface CreateRevisionInput {
  modelKod: string
  masterSize: number
}

export function createSayaRevision(input: CreateRevisionInput): Promise<SayaRevisionDetail> {
  return apiFetch<SayaRevisionDetail>('/saya/revisions', {
    method: 'POST',
    body: JSON.stringify(input),
  })
}

/**
 * Throws a draft away — its sheets, their parts and the stored images with it.
 *
 * Drafts only, which is what makes it safe to offer: an approved revision is
 * immutable and the API refuses. It exists because the master size is fixed at
 * creation and a model may hold one draft at a time, so a mistyped ana beden
 * would otherwise wedge the model's saya tab for good.
 */
export function deleteSayaRevision(revisionId: number): Promise<SayaOkResponse> {
  return apiFetch<SayaOkResponse>(`/saya/revisions/${revisionId}`, { method: 'DELETE' })
}

export interface UploadScanInput {
  revisionId: number
  /** The raw file — the Worker streams it to R2 without buffering it. */
  file: Blob
  pxWidth: number
  pxHeight: number
  checksumSha256?: string
  /**
   * The sheet this upload REPLACES, when the operator is re-shooting one. Left
   * off, the upload adds a sheet to the set and touches nothing that is already
   * there.
   */
  replacesScanId?: number
}

/**
 * Uploads one sheet of the set.
 *
 * The body is the image, not JSON: the dimensions travel as headers so the
 * Worker never has to parse a multipart envelope around a high-resolution scan.
 * The calibration is NOT sent from here at all: the Worker reads the file's own
 * DPI stamp (`worker/lib/saya/scan-stream.ts`) — the factory scanner writes one
 * into every file — and a file without a trustworthy stamp is simply stored
 * uncalibrated, which approval refuses until the sheet is rescanned.
 *
 * A revision holds several sheets, because a saya set does not fit on one bed
 * with the pieces lying apart. Uploading appends; `replacesScanId` re-shoots one
 * sheet and leaves every other sheet's parts and labelling untouched.
 *
 * The checksum is not decoration: an append whose file the revision already
 * stores comes back as that sheet (200) instead of a second copy of it (201),
 * which is what makes pressing "yükle" again after a dropped response safe.
 */
export function uploadSayaScan(input: UploadScanInput): Promise<SayaScanDto> {
  const headers: Record<string, string> = {
    'Content-Type': input.file.type || 'image/png',
    'x-px-width': String(input.pxWidth),
    'x-px-height': String(input.pxHeight),
  }
  if (input.checksumSha256) headers['x-checksum-sha256'] = input.checksumSha256

  const path =
    input.replacesScanId == null
      ? `/saya/revisions/${input.revisionId}/scans`
      : `/saya/scans/${input.replacesScanId}/replace`
  return apiFetch<SayaScanDto>(path, { method: 'POST', body: input.file, headers })
}

/**
 * Removes one sheet from the set — its parts go with it, every other sheet's
 * labelling stays.
 *
 * Drafts only. This is the way out of a sheet that should never have been
 * uploaded (the bed photographed twice, the wrong model, a test scan); a
 * re-shoot swaps one sheet for another and cannot express "this one is simply
 * not part of the set". Deleting the last sheet is allowed: an empty draft is
 * the state a freshly created revision is already in.
 *
 * The answer is the whole revision, because the set's part count and its net
 * desi per material have just moved.
 */
export function deleteSayaScan(scanId: number): Promise<SayaRevisionDetail> {
  return apiFetch<SayaRevisionDetail>(`/saya/scans/${scanId}`, { method: 'DELETE' })
}

/**
 * Replaces this scan's parts wholesale — the draft's geometry is re-derived.
 *
 * The trace's verdict goes with them: it describes exactly these shapes, and a
 * quality that stayed in the tab would be gone on the next reload — leaving a
 * scan that failed its checks rendering as clean and approving as clean.
 */
export function saveSayaScanParts(
  scanId: number,
  parts: TracedPartInput[],
  quality: ScanQuality,
): Promise<SayaPartDto[]> {
  const request: SaveScanPartsRequest = { parts, quality }
  return apiFetch<SayaPartDto[]>(`/saya/scans/${scanId}/parts`, {
    method: 'PUT',
    body: JSON.stringify(request),
  })
}

/**
 * Asks a model what it can see on this sheet, and what it makes of our tracing.
 * Changes nothing.
 *
 * The one call in this file that is not a read or a write: the request carries
 * BOTH pictures — the sheet and our tracing of it, rendered side by side from
 * the bitmap the review canvas already holds — and the Worker answers with
 * suggestions plus a review. It reads no bucket for this; the stored original is
 * still served by `sayaScanImageUrl`, and this call simply stopped asking the
 * server to fetch it again. NOTHING is persisted by either side. What the
 * screen does with them — pre-fill the controls the operator has not answered,
 * flag the paper slips, show the review as a note — is a local edit like any
 * other, and only the screen's own autosave flush reaches the database.
 *
 * Session-gated and nothing more, like the rest of the module. The screen fires
 * it once per traced sheet rather than on a button: the answer is worth most
 * before anybody has started typing, and an operator who has to ask for a check
 * is an operator who skips it on the sheet that needed it.
 */
export function suggestSayaPieces(
  scanId: number,
  request: SuggestScanRequest,
): Promise<SuggestScanResponse> {
  return apiFetch<SuggestScanResponse>(`/saya/scans/${scanId}/suggest`, {
    method: 'POST',
    body: JSON.stringify(request),
  })
}

export interface PartPatch {
  name?: string
  materialId?: number | null
  qtyPerPair?: number
  handedness?: SayaPartDto['handedness']
  orientation?: SayaPartDto['orientation']
  directionVec?: [number, number] | null
  /** Rigid normalization pose; null clears back to identity. */
  pose?: PiecePose | null
  bomRef?: SayaPartDto['bomRef']
}

export function updateSayaPart(partId: number, patch: PartPatch): Promise<SayaPartDto> {
  return apiFetch<SayaPartDto>(`/saya/parts/${partId}`, {
    method: 'PUT',
    body: JSON.stringify(patch),
  })
}

export interface ImportDxfInput {
  revisionId: number
  masterSize: number
  /** The DXF file's text; the Worker parses it with `src/worker/lib/dxf`. */
  dxf: string
}

/**
 * Imports a graded DXF. The answer is the revision PLUS the reader's warnings —
 * what it had to interpret on the way in (an arc read off a bulge factor, a
 * chain the file never closed) — which the screen is expected to show.
 */
export function importSayaDxf(input: ImportDxfInput): Promise<DxfImportResult> {
  return apiFetch<DxfImportResult>(
    `/saya/revisions/${input.revisionId}/import-dxf?masterSize=${encodeURIComponent(String(input.masterSize))}`,
    {
      method: 'POST',
      body: input.dxf,
      headers: { 'Content-Type': 'text/plain; charset=utf-8' },
    },
  )
}
